import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import Layout from "../components/Layout";
import { Link } from "react-router-dom";
import { OutboundLink } from "react-ga";
import classNames from "classnames";
import { initialize } from "../store/pagedata";
import styles from "../styles/privacy.module.scss";
import useDarkMode from "use-dark-mode";
import { useDispatch } from "react-redux";

const Recs = () => {
  const darkMode = useDarkMode(false);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(initialize());
  }, []);

  return (
    <Layout>
      <Helmet>
        <meta name="description" content="EDHREC Privacy Policy" />
        <meta name="keywords" content="EDHREC,privacy,policy" />
        <title>Privacy Policy | EDHREC</title>
      </Helmet>
      <div className={classNames({ [styles.dark]: darkMode.value })}>
        <h4 className="page-heading">Privacy Policy</h4>
        <div className={styles.container}>
          <p>Last updated: January 20th, 2020</p>
          <p>
            GG Software LLC (&quot;us&quot;, &quot;we&quot;, or &quot;our&quot;) operates edhrec.com
            (the &quot;Site&quot;). This page informs you of our policies regarding the collection,
            use and disclosure of Personal Information we receive from users of the Site.
          </p>
          <p>
            We use your Personal Information only for providing and improving the Site. By using the
            Site, you agree to the collection and use of information in accordance with this policy.
          </p>
          <h3>Information Collection And Use</h3>
          <p>
            While using our Site, we may ask you to provide us with certain personally identifiable
            information that can be used to contact or identify you. Personally identifiable
            information may include, but is not limited to your name (&quot;Personal
            Information&quot;).
          </p>
          <p>
            We may use third-party advertising companies to serve ads when you visit the Site. These
            companies may use aggregated information (not including your name, address, email
            address or telephone number) about your visits to this and other Web sites in order to
            provide advertisements about goods and services of interest to you. If you would like
            more information about this practice and to know your choices about not having this
            information used by these companies,&nbsp;
            <OutboundLink
              alt="Opt Out"
              eventLabel="http://www.networkadvertising.org/managing/opt_out.asp"
              target="_blank"
              to="http://www.networkadvertising.org/managing/opt_out.asp"
            >
              click here
            </OutboundLink>
            .
          </p>
          <h3>Log Data</h3>
          <p>
            Like many site operators, we collect information that your browser sends whenever you
            visit our Site (&quot;Log Data&quot;).
          </p>
          <p>
            This Log Data may include information such as your computer&apos;s Internet Protocol
            (&quot;IP&quot;) address, browser type, browser version, the pages of our Site that you
            visit, the time and date of your visit, the time spent on those pages and other
            statistics.
          </p>
          <p>
            In addition, we use third party services such as Google Analytics that collect, monitor
            and analyze this.
          </p>
          <h3>Cookies</h3>
          <p>
            We use cookies to store information for your convenience (&quot;Settings&quot;). We also
            may use third party advertisements on the Site. Some of these advertisers may use
            technology such as cookies and web beacons when they advertise on the Site, which will
            also send these advertisers (such as Google through the Google AdSense program)
            information including your IP address, your ISP , the browser you used to visit our
            site, and in some cases, whether you have Flash installed. This is generally used for
            geotargeting purposes (showing New York real estate ads to someone in New York, for
            example) or showing certain ads based on specific sites visited (such as showing cooking
            ads to someone who frequents cooking sites).
          </p>
          <p>
            You can choose to disable or selectively turn off our cookies or third-party cookies in
            your browser settings, or by managing preferences in programs such as Norton Internet
            Security. However, this can greatly affect how you are able to interact with the Site as
            well as most other websites.
          </p>
          <h3>Security</h3>
          <p>
            The security of your Personal Information is important to us, but remember that no
            method of transmission over the Internet, or method of electronic storage, is 100%
            secure. While we strive to use commercially acceptable means to protect your Personal
            Information, we cannot guarantee its absolute security.
          </p>
          <div className="ccpa">
            <h3>For California Residents: CCPA Information</h3>
            <p>
              <span data-ccpa-link="1"></span>
            </p>
            <p>
              This Privacy Notice for California Residents supplements the information contained
              elsewhere in this Privacy Notice and applies solely to all visitors, users, and others
              who reside in the State of California (“consumers” or “you”). We adopt this notice to
              comply with the California Consumer Privacy Act of 2018 (CCPA) and any terms defined
              in the CCPA have the same meaning when used in this notice.
            </p>
            <h3>Rights and Choices</h3>
            <p>
              The CCPA provides consumers located in the state of California with certain rights
              regarding their personal information and data. The following section describes those
              rights and explains how to exercise them:
            </p>
            <h3>Access to Specific Information and Data Portability Rights</h3>
            <p>
              You have the right to request that the company disclose certain information to you
              about our collection and use of your personal information over the past 12 months.
              Once we receive and confirm your verifiable consumer request (as described in the
              section “Exercising Access, Data Portability, and Deletion Rights”), we will disclose
              to you:
            </p>
            <ul>
              <li>The categories of personal information we collected about you.</li>
              <li>
                The categories of sources for the personal information we collected about you.
              </li>
              <li>
                Our business or commercial purpose for collecting or selling that personal
                information.
              </li>
              <li>The categories of third parties with whom we share that personal information.</li>
              <li>
                The specific pieces of personal information we collected about you (also called data
                portability request)
              </li>
              <li>
                If we sold or disclose your personal information for a business purpose, two
                separate lists disclosing:
                <ul>
                  <li>
                    Sales, identifying the personal information categories that each category of
                    recipient purchased, and
                  </li>
                  <li>
                    Disclosures for a business purpose, identifying the personal information
                    categories that each category of recipient obtained
                  </li>
                </ul>
              </li>
            </ul>
            <h3>Non-Discrimination</h3>
            <p>
              We will not discriminate against you for exercising any of your CCPA rights. Unless
              permitted by the CCPA, we will not:
            </p>
            <ul>
              <li>Deny you goods or services.</li>
              <li>
                Charge you different prices or rates for goods or services, including through
                granting discounts or imposing penalties.
              </li>
              <li>Provide you a different level or quality of goods or services.</li>
              <li>
                Suggest that you may receive a different price or rate for goods or services or a
                different level of quality of goods or services.
              </li>
            </ul>
            <p>
              Any CCPA-permitted financial incentive we offer will reasonably relate to your value
              and contain written terms that describe the program’s material aspects.
            </p>
            <h3>Exercising Access, Data Portability, and Deletion Rights</h3>
            <p>
              To exercise the access, data portability, and deletion rights described above, please
              submit a verifiable consumer request to us by{" "}
              <Link to="/contact-us">contacting us</Link>.
            </p>
            <p>
              Only you, or a person registered with the California Secretary of State that you
              authorize to act on your behalf, may make a verifiable consumer request related to
              your personal information. You may also make a verifiable consumer request on behalf
              of your minor child.
            </p>
            <p>
              You may only make a verifiable consumer request for access of data portability twice
              within a 12-month period. The verifiable consumer request must:
            </p>
            <ul>
              <li>
                Provide sufficient information that allows us to reasonably verify you are the
                person about whom we collected personal information or an authorized representative.
              </li>
              <li>
                Describe your request with sufficient detail that allows us to properly understand,
                evaluate, and respond to it.
              </li>
            </ul>
            <p>
              We cannot respond to your request or provide you with personal information if we
              cannot verify your identity or authority to make the request and confirm the personal
              information relates to you.
            </p>
            <p>
              Making a verifiable consumer request does not require you to create an account with
              us. We will only use personal information provided in a verifiable consumer request to
              verify the requestor’s identity or authority to make the request.
            </p>
            <h3>Information We Collect</h3>
            <p>
              Our websites, emails (with your consent, where required by law), and other products,
              services and platforms collect information that identifies, relates to, describes,
              references, is capable of being associated with, or could reasonably be linked,
              directly or indirectly, with a particular consumer or device (“personal information”).
              In particular, our websites, apps, emails, and other products, services and platforms
              may have collected the following categories of personal information from its consumers
              within the last twelve (12) months:
            </p>
            <table>
              <tr>
                <th>Category</th>
                <th>Collected</th>
              </tr>
              <tr>
                <td>A. Identifiers</td>
                <td>YES</td>
              </tr>
              <tr>
                <td>
                  B. Personal information categories listed in the California Customer Records
                  statute (Cal. Civ. Code § 1798.80(e))
                </td>
                <td>YES</td>
              </tr>
              <tr>
                <td>C. Protected classification characteristics under California or federal law</td>
                <td>NO</td>
              </tr>
              <tr>
                <td>D. Commercial information.</td>
                <td>YES</td>
              </tr>
              <tr>
                <td>E. Biometric information.</td>
                <td>NO</td>
              </tr>
              <tr>
                <td>F. Internet or other similar network activity.</td>
                <td>YES</td>
              </tr>
              <tr>
                <td>G. Geolocation data.</td>
                <td>YES</td>
              </tr>
              <tr>
                <td>H. Sensory data.</td>
                <td>NO</td>
              </tr>
              <tr>
                <td>I. Professional or employment-related information.</td>
                <td>NO</td>
              </tr>
              <tr>
                <td>J. Inferences drawn from other personal information.</td>
                <td>YES</td>
              </tr>
            </table>
            <p>Personal information does not include:</p>
            <ul>
              <li>Publicly available information from government records.</li>
              <li>Deidentified or aggregated consumer information.</li>
              <li>
                Information excluded from the CCPA’s scope, like:
                <ul>
                  <li>
                    health or medical information covered by the Health Insurance Portability and
                    Accountability Act of 1996 (HIPAA) and the California Confidentiality of Medical
                    Information Act (CMIA) or clinical trial data;
                  </li>
                  <li>
                    personal information covered by certain sector-specific privacy laws, including
                    the Fair Credit Reporting Act (FRCA), the Gramm-Leach-Bliley Act (GLBA) or
                    California Financial Information Privacy Act (FIPA), and the Driver’s Privacy.
                  </li>
                </ul>
              </li>
            </ul>
            <h3>Use of Personal Information</h3>
            <p>
              We may use or disclose the personal information we collect for one or more of the
              following business purposes:
            </p>
            <ul>
              <li>
                To fulfill or meet the reason you provided the information. For example, if you
                share your name and contact information to request a newsletter or ask a question
                about our products or services, we will use that personal information to respond to
                your inquiry. If you provide your personal information to purchase a product or
                service, we or our third-party service providers will use that information to
                process your payment and facilitate delivery. We may also save your information to
                facilitate new product or service orders and requests.
              </li>
              <li>
                To provide, support, personalize, and develop our websites, emails, and other
                products, services and platforms.
              </li>
              <li>To create, maintain, customize, and secure your account with us.</li>
              <li>
                To process your requests, purchases, transactions, and payments and prevent
                transactional fraud.
              </li>
              <li>
                To provide you with support and to respond to your inquiries, including
                investigating and addressing your concerns and monitoring and improving our
                responses.
              </li>
              <li>
                To personalize your website, apps, emails, or other product, service or platform
                experience and to deliver content and product and service offerings relevant to your
                interests, including targeted offers and ads through our websites, apps, emails, and
                other products, services and platforms.
              </li>
              <li>
                To help maintain the safety, security, and integrity of our websites, apps, emails,
                and other products, services and platforms, databases and other technology assets,
                and business.
              </li>
              <li>
                For testing, research, analysis, and product development, including to develop and
                improve our websites, apps, emails, and other products, services and platforms.
              </li>
              <li>
                To respond to law enforcement requests and as required by applicable law, court
                order, or governmental regulations.
              </li>
              <li>
                As described to you when collecting your personal information or as otherwise set
                forth in the CCPA.
              </li>
              <li>
                To evaluate or conduct a merger, divestiture, restructuring, reorganization,
                dissolution, or other sale or transfer of some or all of the Company’s assets,
                whether as a going concern or as part of bankruptcy, liquidation, or similar
                proceeding, in which personal information held by the Company about our Website
                users is among the assets transferred.
              </li>
            </ul>
            <p>
              The Company will not collect additional categories of personal information or use the
              personal information we collected for materially different, unrelated, or incompatible
              purposes without providing you notice.
            </p>
            <h3>Sharing Personal Information</h3>
            <p>
              The Company may disclose your personal information to a third-party for a business
              purpose or sell your personal information, subject to your right to opt-out of those
              sales (see ‘Sales of Personal Information’ below). When we disclose personal
              information for a business purpose, we enter a contract that describes the purpose and
              requires the recipient to both keep that personal information confidential and not use
              it for any purpose except performing the contract. The CCPA prohibits third parties
              who purchase the personal information we hold from reselling it unless you have
              received explicit notice and an opportunity to opt-out of further sales (see ‘Sales of
              Personal Information’ below).
            </p>
            <p>
              We may share your personal information with the following categories of third parties:
            </p>
            <ul>
              <li>Subsidiaries and affiliates.</li>
              <li>Contractors and service providers.</li>
              <li>Data aggregators.</li>
              <li>Third parties with whom we partner to offer products and services to you.</li>
            </ul>
            <h3>Disclosures of Personal Information for a Business Purpose</h3>
            <p>
              In the preceding twelve (12) months, the Company has disclosed the following
              categories of personal information for a business purpose:
            </p>
            <ul>
              <li>[Category A: Identifiers.]</li>
              <li>[Category B: California Customer Records personal information categories.]</li>
              <li>[Category F: Internet or other similar network activity.]</li>
              <li>[Category G: Geolocation Data.]</li>
              <li>[Category K: Inferences drawn from other personal information.]</li>
            </ul>
            <h3>Sales of Personal Information</h3>
            <p>
              In the preceding twelve (12) months, the company has sold the following categories of
              personal information collected through our ad-supported services:
            </p>
            <ul>
              <li>[Category A: Identifiers.]</li>
              <li>[Category F: Internet or other similar network activity.]</li>
              <li>[Category G: Geolocation Data.]</li>
              <li>[Category K: Inferences drawn from other personal information.]</li>
            </ul>
            <p>
              The company and our advertising partners collect the personal information identified
              above (such as the cookies stored on your browser, the advertising identifier on your
              mobile device, or the IP address of your device) when you visit our websites, apps,
              and other products, services and platforms, or open our emails. We, and our partners,
              use this information to tailor and deliver ads to you on our websites, apps, emails,
              and other products, services and platforms, or to help tailor ads to you when you
              visit others’ sites (or use others’ apps). To tailor ads that may be more relevant to
              you, we and/or our partners may share the information we collect with third parties.
            </p>
          </div>
          <h3>Changes To This Privacy Policy</h3>
          <p>
            We reserve the right to update or change our Privacy Policy at any time and you should
            check this Privacy Policy periodically. Your continued use of the Service after we post
            any modifications to the Privacy Policy on this page will constitute your acknowledgment
            of the modifications and your consent to abide and be bound by the modified Privacy
            Policy.
          </p>
          <h3>Contact Us</h3>
          <p>
            If you have any questions about this Privacy Policy, please&nbsp;
            <Link as="/contact-us" href="/contact-us">
              <a>contact us</a>
            </Link>
            .
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default Recs;
